"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isSameOrigin = exports.buildUnauthenticatedV2RequestConfig = exports.parseChallengeHeaders = exports.getToken = exports.paginatedV2CallWithLastMarker = exports.paginatedV2Call = exports.registryCall = exports.registryV2Call = void 0;
const tslib_1 = require("tslib");
const parseLink = require("parse-link-header");
const needle_1 = require("./needle");
const realm_validation_1 = require("./realm-validation");
const options_1 = require("./options");
const BEARER_REALM = "Bearer realm";
const MAX_RETRIES = process.env.DOCKER_REGISTRY_V2_CLIENT_MAX_RETRIES
    ? parseInt(process.env.DOCKER_REGISTRY_V2_CLIENT_MAX_RETRIES, 10)
    : 1;
const MAX_REDIRECTS = 5;
/**
 * Makes an authenticated request to a Docker Registry V2 endpoint.
 *
 * Sends an HTTP request to a Docker Registry V2 API endpoint with automatic authentication handling.
 * Handles 401 responses by negotiating bearer tokens, and follows HTTP redirects (up to 5 redirects).
 * Implements exponential backoff for rate-limited responses (429, 503, 502, 504 status codes).
 *
 * OCI Distribution Specification: https://github.com/opencontainers/distribution-spec
 *
 * @param registryBase - The base URL of the container registry (without scheme)
 * @param endpoint - The API endpoint path (e.g., "/v2/", "/library/nginx/manifests/latest")
 * @param accept - Accept header value for content negotiation (e.g., "application/vnd.docker.distribution.manifest.v2+json")
 * @param observer - Optional observer to record client events
 * @param username - Optional username for authentication
 * @param password - Optional password for authentication
 * @param reqOptions - Optional request configuration options (allowedHosts, disallowDangerousHosts, etc.)
 *
 * @returns HTTP response from the registry
 *
 * @throws {NeedleWrapperException} When HTTP errors occur after retries
 * @throws {Error} When registry connectivity or validation fails
 *
 * @internal
 */
async function registryV2Call(registryBase, endpoint, accept, username, password, reqOptions = {}, observer, blobMode) {
    const opts = (0, options_1.cloneOptions)(reqOptions);
    const reqConfig = buildUnauthenticatedV2RequestConfigInternal(registryBase, endpoint, accept, opts);
    applyBlobMode(reqConfig, blobMode);
    try {
        return await (0, needle_1.needleWrapper)(reqConfig, MAX_RETRIES, observer);
    }
    catch (err) {
        if (err.statusCode === 401) {
            if (!username || !password) {
                // Supply and empty username and password if no credentials
                // are provided. These might be added later by a broker client.
                username = username ? username : "";
                password = password ? password : "";
            }
            const authConfig = await setAuthConfig(registryBase, err, reqConfig, username, password, opts, observer);
            try {
                return await (0, needle_1.needleWrapper)(authConfig, MAX_RETRIES, observer);
            }
            catch (err) {
                if (isRedirectStatus(err.statusCode)) {
                    return await handleRedirect(err, reqConfig, observer);
                }
                throw err;
            }
        }
        if (isRedirectStatus(err.statusCode)) {
            return await handleRedirect(err, reqConfig, observer);
        }
        throw err;
    }
}
exports.registryV2Call = registryV2Call;
/**
 * Makes an authenticated request to any HTTP endpoint using registry authentication mechanisms.
 *
 * A general-purpose HTTP client for making authenticated requests to arbitrary endpoints
 * using the Docker Registry V2 authentication flow. This function supports bearer token
 * authentication and basic authentication, making it useful for registry-related endpoints
 * that aren't part of the core OCI Distribution Specification API.
 *
 * Warning: This function is considered technical debt and may be removed in future versions.
 * It was created for a specific use case (MAGMA-1262) and abuses the library's registry-specific API.
 *
 * @param uri - Full endpoint URI or host (will be prefixed with https://)
 * @param username - Optional username for authentication
 * @param password - Optional password for authentication
 * @param reqOptions - Optional request configuration options
 * @param observer - Optional observer to record client events
 *
 * @returns HTTP response from the endpoint
 *
 * @throws {NeedleWrapperException} When HTTP errors occur
 * @throws {Error} When connectivity fails
 */
async function registryCall(uri, username, password, reqOptions = {}, observer) {
    const opts = (0, options_1.cloneOptions)(reqOptions);
    const reqConfig = applyRequestOptions({ uri: `https://${uri}` }, opts);
    try {
        return await (0, needle_1.needleWrapper)(reqConfig, MAX_RETRIES, observer);
    }
    catch (err) {
        if (err.statusCode === 401) {
            if (!username || !password) {
                // Supply and empty username and password if no credentials
                // are provided. These might be added later by a broker client.
                username = username ? username : "";
                password = password ? password : "";
            }
            const authConfig = await setAuthConfig("", err, reqConfig, username, password, opts, observer);
            try {
                return await (0, needle_1.needleWrapper)(authConfig, MAX_RETRIES, observer);
            }
            catch (err) {
                if (isRedirectStatus(err.statusCode)) {
                    return await handleRedirect(err, reqConfig, observer);
                }
                throw err;
            }
        }
        if (isRedirectStatus(err.statusCode)) {
            return await handleRedirect(err, reqConfig, observer);
        }
        throw err;
    }
}
exports.registryCall = registryCall;
/**
 * Makes paginated requests to a Docker Registry V2 endpoint using link-header pagination.
 *
 * Retrieves paginated results from registry endpoints that support the link-header pagination
 * mechanism (RFC 5988). This is the traditional pagination approach used by Docker Registry V2.
 * Automatically follows pagination links until all results are retrieved or maxPages is reached.
 *
 * @param registryBase - The base URL of the container registry
 * @param accept - Accept header for content negotiation
 * @param username - Username for authentication
 * @param password - Password for authentication
 * @param endpoint - The API endpoint path
 * @param key - JSON response key containing the array of results (e.g., "repositories", "tags")
 * @param pageSize - Results per page (default: 1000)
 * @param maxPages - Maximum pages to fetch (default: Number.MAX_SAFE_INTEGER)
 * @param reqOptions - Optional request configuration
 * @param observer - Optional observer to record client events
 *
 * @returns Combined array of all results from all pages
 *
 * @throws {NeedleWrapperException} When HTTP errors occur
 *
 * @internal
 */
async function paginatedV2Call(registryBase, accept, username, password, endpoint, key, pageSize = 1000, maxPages = Number.MAX_SAFE_INTEGER, reqOptions = {}, observer) {
    const opts = (0, options_1.cloneOptions)(reqOptions);
    const result = [];
    let pageEndpoint = `${endpoint}?n=${pageSize}`;
    let pageCount = 0;
    while (pageCount < maxPages) {
        const response = await registryV2Call(registryBase, pageEndpoint, accept, username, password, opts, observer);
        const body = (0, needle_1.parseResponseBody)(response);
        if (body && body[key]) {
            result.push(...body[key]);
        }
        if (!response.headers.link) {
            break;
        }
        pageCount += 1;
        pageEndpoint = pageEndpointForLink(endpoint, response.headers.link);
    }
    return result;
}
exports.paginatedV2Call = paginatedV2Call;
/**
 * Makes paginated requests to a Docker Registry V2 endpoint using "last marker" pagination.
 *
 * Retrieves paginated results from registry endpoints using the "last" marker approach,
 * which correctly implements the Docker Registry V2 API pagination pattern. This method
 * uses the "last" query parameter to indicate the last item from the previous response,
 * allowing for unbounded retrieval of resources without traditional Link header limitations.
 * This approach is more robust than link-header pagination and is recommended for registries
 * with large result sets.
 *
 * @param registryBase - The base URL of the container registry
 * @param accept - Accept header for content negotiation
 * @param username - Username for authentication
 * @param password - Password for authentication
 * @param endpoint - The API endpoint path
 * @param key - JSON response key containing the array of results (e.g., "repositories", "tags")
 * @param pageSize - Results per page (default: 1000)
 * @param reqOptions - Optional request configuration
 * @param observer - Optional observer to record client events
 *
 * @returns Combined array of all results from all pages
 *
 * @throws {NeedleWrapperException} When HTTP errors occur
 *
 * @see https://distribution.github.io/distribution/spec/api/#pagination-1
 *
 * @internal
 */
async function paginatedV2CallWithLastMarker(registryBase, accept, username, password, endpoint, key, pageSize = 1000, reqOptions = {}, observer) {
    const opts = (0, options_1.cloneOptions)(reqOptions);
    const result = [];
    let lastItem = undefined; // if this is undefined, we're on the first page so it has to be optional
    let shouldContinue = true;
    while (shouldContinue) {
        const queryParams = new URLSearchParams();
        queryParams.set("n", pageSize.toString());
        if (lastItem) {
            queryParams.set("last", lastItem);
        }
        const pageEndpoint = `${endpoint}?${queryParams.toString()}`;
        const response = await registryV2Call(registryBase, pageEndpoint, accept, username, password, opts, observer);
        const body = (0, needle_1.parseResponseBody)(response);
        // Exit early if we don't have valid response data
        if (!body || !body[key] || !Array.isArray(body[key])) {
            shouldContinue = false;
            continue;
        }
        const items = body[key];
        result.push(...items);
        shouldContinue = items.length === pageSize;
        // set the last item for the next iteration (if there will be one)
        if (shouldContinue) {
            lastItem = items[items.length - 1];
        }
    }
    return result;
}
exports.paginatedV2CallWithLastMarker = paginatedV2CallWithLastMarker;
/** @internal */
async function getToken(registryBase, authBase, service, scope, username, password, reqOptions = {}) {
    const opts = (0, options_1.cloneOptions)(reqOptions);
    // Build the auth request without letting the caller override:
    //   - the auth uri (must be `authBase`)
    //   - the `service` / `scope` query string keys (must be the values negotiated
    //     from the WWW-Authenticate header)
    // Any other keys in `opts.qs` are preserved.
    // `opts` is a library-owned deep clone, so we can mutate it in place.
    const mergedQs = Object.assign(Object.assign({}, (typeof opts.qs === "object" && opts.qs !== null ? opts.qs : {})), { service,
        scope });
    delete opts.qs;
    const reqConfig = applyRequestOptions({
        uri: authBase,
        qs: mergedQs,
    }, opts);
    // Test truthiness, should be false when username and password are undefined
    if (username && password) {
        reqConfig.username = username;
        reqConfig.password = password;
    }
    const response = await (0, needle_1.needleWrapper)(Object.assign(Object.assign({}, reqConfig), { 
        // we always want to read the credentials through the body
        snykInternalOutputStream: undefined }));
    const body = (0, needle_1.parseResponseBody)(response);
    return body.token || body.access_token;
}
exports.getToken = getToken;
/** @internal */
function parseChallengeHeaders(challengeHeaders) {
    const headersMap = Object.create(null);
    const headerSplit = challengeHeaders.split(",");
    for (let i = 0; i < headerSplit.length; i++) {
        const entry = headerSplit[i];
        if (!entry.includes("=") && i > 0) {
            // we'll get here in case a value includes a comma. we want to concat what's after the comma to the previous value
            headerSplit[i - 1] += `,${entry}`;
            headerSplit.splice(i, 1);
            i--;
        }
    }
    headerSplit.forEach(entry => {
        if (!entry.includes("="))
            return;
        const [rawKey, ...valueParts] = entry.split("=");
        const key = rawKey.trim();
        const rawValue = valueParts.join("=").trim();
        if (rawValue.startsWith('"') && rawValue.endsWith('"')) {
            headersMap[key] = rawValue.slice(1, -1);
        }
        else {
            headersMap[key] = rawValue;
        }
    });
    return [headersMap[BEARER_REALM], headersMap.service, headersMap.scope];
}
exports.parseChallengeHeaders = parseChallengeHeaders;
/** @internal */
function buildUnauthenticatedV2RequestConfig(registryBase, endpoint, accept, reqOptions = {}) {
    const opts = (0, options_1.cloneOptions)(reqOptions);
    return buildUnauthenticatedV2RequestConfigInternal(registryBase, endpoint, accept, opts);
}
exports.buildUnauthenticatedV2RequestConfig = buildUnauthenticatedV2RequestConfig;
function buildUnauthenticatedV2RequestConfigInternal(registryBase, endpoint, accept, opts) {
    return applyRequestOptions({
        headers: { Accept: accept },
        uri: `https://${registryBase}/v2${endpoint}`,
    }, opts);
}
/**
 * Translate the `BlobTransferMode` into the concrete Needle
 * field required for blob transfers.
 */
function applyBlobMode(reqConfig, mode) {
    if (!mode)
        return;
    reqConfig.json = false;
    reqConfig.encoding = null;
    if (mode.kind === "streamed") {
        reqConfig.snykInternalOutputStream = mode.outputPath;
    }
}
function pageEndpointForLink(endpoint, link) {
    const linkPath = parseLink(link).next.url;
    const linkQuery = linkPath.split("?")[1];
    return `${endpoint}?${linkQuery}`;
}
async function setAuthConfig(registryBase, err, reqConfig, username, password, opts = {}, observer) {
    // See: https://docs.docker.com/registry/spec/auth/token/#how-to-authenticate
    const challengeHeaders = err.headers["www-authenticate"];
    if (!challengeHeaders) {
        throw err;
    }
    const [authBase, service, scope] = parseChallengeHeaders(challengeHeaders);
    if (!authBase) {
        // basic auth
        return Object.assign(Object.assign({}, reqConfig), { username, password });
    }
    else {
        // Bearer token
        // Validate realm URL against reqConfig.uri as fallback when registryBase is empty
        const parsedReqUri = new URL(reqConfig.uri);
        const effectiveHost = registryBase ? registryBase : parsedReqUri.hostname;
        (0, realm_validation_1.assertValidRealm)(effectiveHost, authBase, parsedReqUri.protocol, opts, observer);
        const token = await getToken(registryBase, authBase, service, scope, username, password, opts);
        return Object.assign(Object.assign({}, reqConfig), { headers: Object.assign(Object.assign({}, reqConfig.headers), { Authorization: `Bearer ${token}` }) });
    }
}
// Handle redirect while following up to MAX_REDIRECTS
async function handleRedirect(err, config, observer) {
    return handleRedirectRec(err, config, MAX_REDIRECTS, observer);
}
async function handleRedirectRec(err, config, maxRedirects = 0, observer) {
    if (maxRedirects === 0) {
        throw err;
    }
    const location = err.headers.location;
    if (!location) {
        throw err;
    }
    const redirectUrl = assertFullUrl(location, config.uri);
    // check if artifactory is redirecting to landing page (deactivated instance, see #incident-2244-test-jfrog-artifactory-inactive for more details)
    if (redirectUrl.includes("landing.jfrog.com")) {
        throw new Error(`Artifactory instance appears to be deactivated (redirected to ${location})`);
    }
    // Check if GCR is redirecting to documentation (e.g., when the _catalog endpoint is unavailable or restricted)
    if (redirectUrl.includes("cloud.google.com/artifacts/docs") ||
        redirectUrl.includes("cloud.google.com/artifact-registry/docs")) {
        throw new Error(`GCR instance appears to be unavailable (redirected to documentation: ${location})`);
    }
    if (!isSameOrigin(redirectUrl, config.uri)) {
        delete config.headers.Authorization;
        delete config.headers.authorization;
    }
    config.uri = redirectUrl;
    try {
        return await (0, needle_1.needleWrapper)(config, MAX_RETRIES, observer);
    }
    catch (err) {
        if (isRedirectStatus(err.statusCode)) {
            return await handleRedirectRec(err, config, maxRedirects - 1, observer);
        }
        throw err;
    }
}
/*
 * Takes request config and applies allowed options to it.
 *
 * Library-only fields (`protocol`, `hostMappings`, `acceptManifest`,
 * `acceptLayer`, `ssrfProtectionDryRun`, `headers`) are consumed here and
 * deliberately excluded from the returned request config so they do not
 * appear on the object handed to Needle.
 *
 * @param reqConfig - request config that is passed to the request library.
 * @param opts - library-owned options (already deep-cloned by `cloneOptions`).
 */
function applyRequestOptions(reqConfig, opts) {
    const { protocol, hostMappings, headers: optionHeaders, acceptManifest: _acceptManifest, acceptLayer: _acceptLayer, ssrfProtectionDryRun: _ssrfProtectionDryRun } = opts, rest = tslib_1.__rest(opts, ["protocol", "hostMappings", "headers", "acceptManifest", "acceptLayer", "ssrfProtectionDryRun"]);
    let uri = applyUriProtocol(reqConfig.uri, protocol);
    uri = applyUriHostMappings(uri, hostMappings);
    const headers = applyHeaders(reqConfig.headers, optionHeaders);
    return Object.assign(Object.assign(Object.assign({}, reqConfig), rest), { uri,
        headers });
}
function applyUriProtocol(uri, protocol) {
    if (!protocol) {
        return uri;
    }
    const updatedUrl = new URL(uri);
    updatedUrl.protocol = protocol;
    return updatedUrl.toString();
}
/**
 * Applies host mappings to given uri.
 *
 * @param uri
 * @param mappings - Array of mappings. Each mapping is represented as array
 *                   tuple: [host_regex_matcher, new_host].
 */
function applyUriHostMappings(uri, mappings) {
    if (!mappings) {
        return uri;
    }
    const updatedUrl = new URL(uri);
    const mapping = mappings.find(([matcher]) => updatedUrl.host.match(matcher));
    if (!mapping) {
        return uri;
    }
    updatedUrl.host = mapping[1];
    return updatedUrl.toString();
}
function applyHeaders(currentHeaders, addHeaders) {
    return Object.assign(Object.assign({}, (currentHeaders || {})), (addHeaders || {}));
}
function assertFullUrl(redirectLocation, originalLocation) {
    let redirectUrl;
    try {
        redirectUrl = new URL(redirectLocation);
    }
    catch (_a) {
        // in case the location is not a proper URL (contains just the path), attach the original URL's origin
        const originalUrl = new URL(originalLocation);
        redirectUrl = new URL(redirectLocation, originalUrl.origin);
    }
    return redirectUrl.href;
}
/*
 Check whether the response status requires redirect.
 Note: since we only make GET requests, we do not need to handle
 different redirect statuses having different requirements for
 handling the request method.
*/
function isRedirectStatus(statusCode) {
    return (statusCode === 301 ||
        statusCode === 302 ||
        statusCode === 303 ||
        statusCode === 307 ||
        statusCode === 308);
}
/**
 * Compares two URLs to determine if they have the same origin.
 *
 * RFC 7231 guidance: When a redirect is received, credentials (Authorization header)
 * should only be forwarded to the same origin.
 *
 * @param urlA - First URL (typically the original request URI)
 * @param urlB - Second URL (typically the redirect location)
 * @returns true if both URLs have the same origin, false otherwise
 *
 * @internal
 */
function isSameOrigin(urlA, urlB) {
    try {
        const parsedA = new URL(urlA);
        const parsedB = new URL(urlB);
        return parsedA.origin === parsedB.origin;
    }
    catch (_a) {
        // If either URL is invalid, they're not the same origin
        return false;
    }
}
exports.isSameOrigin = isSameOrigin;
//# sourceMappingURL=registry-call.js.map